import { motion } from 'framer-motion';
import { gsap } from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import { useEffect, useRef, useState } from 'react';

gsap.registerPlugin(ScrollTrigger);

const HorizontalScroll = () => {
  const containerRef = useRef(null);
  const scrollRef = useRef(null);
  const [isMobile, setIsMobile] = useState(false);

  useEffect(() => {
    // Check if mobile on mount and resize
    const checkMobile = () => {
      setIsMobile(window.innerWidth < 1024);
    };

    checkMobile();
    window.addEventListener('resize', checkMobile);

    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  useEffect(() => {
    // Only apply horizontal scroll on desktop
    if (isMobile) return;

    const container = containerRef.current;
    const scrollContent = scrollRef.current;

    if (!container || !scrollContent) return;

    const scrollWidth = scrollContent.scrollWidth - window.innerWidth;

    const tl = gsap.timeline({
      scrollTrigger: {
        trigger: container,
        start: 'top top',
        end: () => `+=${scrollWidth}`,
        scrub: 1,
        pin: true,
        anticipatePin: 1,
        invalidateOnRefresh: true,
      }
    });

    tl.to(scrollContent, {
      x: -scrollWidth,
      ease: 'none'
    });

    return () => {
      ScrollTrigger.getAll().forEach(trigger => trigger.kill());
    };
  }, [isMobile]);

  const items = [
    {
      title: 'Websites',
      number: '01',
      description: 'We build impactful experiences through great UX, design, and development.',
      color: 'from-orange-500/20 to-red-500/20',
      accentColor: 'orange'
    },
    {
      title: 'Creative',
      number: '02',
      description: 'We design visually-engaging marketing assets to help drive your brand forward.',
      color: 'from-purple-500/20 to-pink-500/20',
      accentColor: 'purple'
    },
    {
      title: 'Strategy',
      number: '03',
      description: 'We identify businesses challenges and develop solutions to achieve your goals.',
      color: 'from-blue-500/20 to-cyan-500/20',
      accentColor: 'blue'
    },
    {
      title: 'Development',
      number: '04',
      description: 'We craft cutting-edge digital solutions with modern technologies.',
      color: 'from-green-500/20 to-emerald-500/20',
      accentColor: 'green'
    }
  ];

  const getAccentColorClass = (color) => {
    const colors = {
      orange: 'group-hover:text-orange-500',
      purple: 'group-hover:text-purple-500',
      blue: 'group-hover:text-blue-500',
      green: 'group-hover:text-green-500'
    };
    return colors[color] || 'group-hover:text-orange-500';
  };

  // Mobile layout - vertical stacking
  if (isMobile) {
    return (
      <div className="bg-[#0f0f0f] py-12 px-4 sm:px-6">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="text-center mb-12"
        >
          <h2 className="text-3xl sm:text-4xl font-bold text-white mb-4 tracking-wider">
            Our Services
          </h2>
          <p className="text-gray-400 text-sm sm:text-base">
            Swipe through our expertise
          </p>
        </motion.div>

        <div className="space-y-8 max-w-lg mx-auto">
          {items.map((item, index) => (
            <motion.div
              key={index}
              initial={{ opacity: 0, y: 50 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-100px" }}
              transition={{ duration: 0.6, delay: index * 0.1 }}
              className="group relative bg-gradient-to-br from-gray-900/50 to-black/50 backdrop-blur-sm border border-gray-800 rounded-2xl p-6 sm:p-8 hover:border-gray-700 transition-all duration-500"
            >
              {/* Background gradient */}
              <div className={`absolute inset-0 bg-gradient-to-br ${item.color} blur-2xl opacity-0 group-hover:opacity-50 rounded-2xl transition-opacity duration-500`} />

              {/* Number badge */}
              <motion.div
                className="absolute -top-4 -right-4 w-16 h-16 sm:w-20 sm:h-20 rounded-full bg-gradient-to-br from-gray-800 to-gray-900 border border-gray-700 flex items-center justify-center"
                whileHover={{ scale: 1.1, rotate: 10 }}
              >
                <span className="text-2xl sm:text-3xl font-bold text-white/30">
                  {item.number}
                </span>
              </motion.div>

              {/* Content */}
              <div className="relative z-10">
                <h3 className={`text-3xl sm:text-4xl font-bold text-white mb-4 tracking-wider transition-colors duration-300 ${getAccentColorClass(item.accentColor)}`}>
                  {item.title}
                </h3>
                <p className="text-gray-400 text-sm sm:text-base leading-relaxed">
                  {item.description}
                </p>
              </div>

              {/* Decorative corner */}
              <div className="absolute bottom-0 right-0 w-24 h-24 opacity-5">
                <svg viewBox="0 0 100 100" className="w-full h-full">
                  <circle cx="100" cy="100" r="80" fill="currentColor" className="text-white" />
                </svg>
              </div>
            </motion.div>
          ))}
        </div>
      </div>
    );
  }

  // Desktop layout - horizontal scroll
  return (
    <div ref={containerRef} className="relative bg-[#0f0f0f] overflow-hidden">
      <div ref={scrollRef} className="flex h-screen">
        {items.map((item, index) => (
          <div
            key={index}
            className="min-w-screen h-screen flex items-center justify-center px-8 lg:px-12 xl:px-20 relative group"
          >
            <motion.div
              initial={{ opacity: 0, scale: 0.8 }}
              whileInView={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.8 }}
              className="relative max-w-3xl"
            >
              {/* Enhanced background gradient */}
              <motion.div
                className={`absolute inset-0 bg-gradient-to-br ${item.color} blur-3xl opacity-30 rounded-full`}
                animate={{
                  scale: [1, 1.2, 1],
                  opacity: [0.3, 0.5, 0.3]
                }}
                transition={{
                  duration: 4,
                  repeat: Infinity,
                  ease: "easeInOut"
                }}
              />

              {/* Number - more responsive */}
              <motion.div
                initial={{ opacity: 0, x: -50 }}
                whileInView={{ opacity: 0.08, x: 0 }}
                transition={{ duration: 1, delay: 0.2 }}
                className="absolute -top-12 lg:-top-20 -left-8 lg:-left-20 text-[120px] lg:text-[200px] xl:text-[240px] font-bold text-white pointer-events-none select-none"
              >
                {item.number}
              </motion.div>

              {/* Content */}
              <div className="relative z-10">
                <motion.h3
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.8, delay: 0.3 }}
                  className="text-5xl lg:text-7xl xl:text-8xl font-bold text-white mb-6 lg:mb-8 tracking-wider"
                >
                  {item.title.split('').map((char, i) => (
                    <motion.span
                      key={i}
                      initial={{ opacity: 0, y: 20 }}
                      whileInView={{ opacity: 1, y: 0 }}
                      transition={{ duration: 0.5, delay: 0.3 + i * 0.05 }}
                      className={`inline-block transition-colors duration-300 ${getAccentColorClass(item.accentColor)}`}
                    >
                      {char}
                    </motion.span>
                  ))}
                </motion.h3>

                <motion.p
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.8, delay: 0.5 }}
                  className="text-gray-400 text-lg lg:text-xl xl:text-2xl leading-relaxed max-w-2xl"
                >
                  {item.description}
                </motion.p>

                {/* Decorative line */}
                <motion.div
                  initial={{ width: 0 }}
                  whileInView={{ width: '100%' }}
                  transition={{ duration: 1, delay: 0.7 }}
                  className="h-0.5 bg-gradient-to-r from-gray-700 via-gray-600 to-transparent mt-8 max-w-md"
                />
              </div>
            </motion.div>
          </div>
        ))}
      </div>

      {/* Enhanced scroll indicator */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 1 }}
        className="absolute bottom-8 lg:bottom-12 left-1/2 -translate-x-1/2 flex items-center gap-3 lg:gap-4 text-white text-xs lg:text-sm tracking-wider"
      >
        <motion.div
          animate={{ x: [0, 10, 0] }}
          transition={{ duration: 2, repeat: Infinity }}
          className="text-lg lg:text-xl"
        >
          →
        </motion.div>
        <span className="font-light">SCROLL HORIZONTALLY</span>
        <motion.div
          animate={{ x: [0, 10, 0] }}
          transition={{ duration: 2, repeat: Infinity, delay: 0.5 }}
          className="text-lg lg:text-xl"
        >
          →
        </motion.div>
      </motion.div>

      {/* Progress indicator */}
      <div className="absolute top-8 right-8 lg:top-12 lg:right-12 flex gap-2">
        {items.map((_, index) => (
          <motion.div
            key={index}
            initial={{ opacity: 0, scale: 0 }}
            animate={{ opacity: 0.3, scale: 1 }}
            transition={{ delay: 0.5 + index * 0.1 }}
            className="w-2 h-2 rounded-full bg-white"
          />
        ))}
      </div>
    </div>
  );
};

export default HorizontalScroll;
